import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppNotificationService } from './app-notification.service';
import { ApiError } from './tech.model';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
	readonly forbiddenMessage =
		'Sie besitzen nicht die notwendigen Rechte für den Zugriff auf die angeforderte Ressource.';
	readonly copyAttachment: string =
		'\n\nBitte kopieren Sie die Details zum Fehler in eine E-Mail und senden Sie diese an Ihren technischen Support.';

	constructor(private notificationService: AppNotificationService) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(req).pipe(
			catchError((response: HttpErrorResponse) => {
				if (response.error instanceof Blob) {
					this.convertBlobResponse(response).then((error: ApiError) => {
						this.handleError(response.status, error);
					});
				} else {
					this.handleError(response.status, response.error);
				}
				return throwError(() => response);
			})
		);
	}

	private handleError(status: number, error: ApiError) {
		if (!this.displaySufficientSpecialMessage(status, error)) {
			const rawMessage = status === 403 ? this.forbiddenMessage : undefined;
			this.notificationService.handleError(this.createErrorMessage(rawMessage), error.id, error);
		}
	}

	private createErrorMessage(message?: string) {
		return message ? message + this.copyAttachment : this.copyAttachment;
	}

	private convertBlobResponse(err: HttpErrorResponse): Promise<ApiError> {
		return new Promise<any>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e: Event) => {
				try {
					const errmsg = JSON.parse((<any>e.target).result);
					resolve(errmsg);
				} catch (e) {
					reject(err);
				}
			};
			reader.onerror = _e => {
				reject(err);
			};
			reader.readAsText(err.error);
		});
	}

	/**
	 * Displays a message with special content which contains all relevant information so that id and attachments should not be displayed.
	 * @param status
	 * @param error
	 * @return true if sufficient message handling has been done
	 */
	private displaySufficientSpecialMessage(status: number, error: ApiError): boolean {
		if (!error) {
			return false;
		}
		const msg = error.issues?.[0]?.message ?? 'no message';
		const code = error.issues?.[0]?.messageCode ?? 'no message code';

		if (code === 'verfahren.duplicate.aktenzeichen') {
			return true; // Anzeige ist bereits im Effect erfolgt
		}
		if (status === 413) {
			this.notificationService.handleError('Die Dateigröße darf 1 MB nicht überschreiten.');
			return true;
		}
		if (status === 404 && msg === 'next available u-signal') {
			this.notificationService.handleError('Es stehen keine freien U-Signale mehr zur Verfügung');
			return true;
		}
		if (status === 400 && msg.endsWith('.')) {
			// hat fertigen Satz zur Anzeige
			this.notificationService.handleError(msg);
			return true;
		}
		return false;
	}
}
